import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ArrowLeft, LogOut, MapPin, Store, Layers, Eye, Globe, Package, MessageSquare } from "lucide-react";

const PLAN_CLS = {
  FREE: "bg-white/10 text-white/80 border-white/20",
  LIGHT: "bg-sky-400/20 text-sky-100 border-sky-300/40",
  WEBPRO: "bg-fundio-gold/25 text-fundio-cream border-fundio-gold/50",
  MULTI: "bg-violet-400/20 text-violet-100 border-violet-300/40",
};

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-fundio-border bg-white p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-fundio-muted"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="text-2xl font-semibold text-fundio-ink">{value ?? "—"}</div>
    </div>
  );
}

export default function Cockpit() {
  const [token] = useState(() => {
    const m = (typeof window !== "undefined" ? (window.location.hash || "") : "").match(/t=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState("all");

  useEffect(() => {
    if (token) window.history.replaceState(null, "", window.location.pathname);
  }, [token]);

  const authCfg = useCallback(() => ({ headers: { "X-Support-Token": token } }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const s = await api.get("/cockpit/session", authCfg());
      setSession(s.data);
      const o = await api.get("/cockpit/overview", authCfg());
      setData(o.data);
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Support-Session ungültig oder abgelaufen.");
    }
  }, [token, authCfg]);
  useEffect(() => { load(); }, [load]);

  const backToControl = () => { if (window.opener) { window.close(); } window.location.href = "/"; };
  const endSession = async () => {
    try { await api.post("/cockpit/session/end", {}, authCfg()); } catch { /* ignore */ }
    toast.success("Support-Session beendet");
    backToControl();
  };

  if (!token || err) return (
    <div className="flex min-h-screen items-center justify-center bg-fundio-dark2 p-6">
      <div data-testid="cockpit-error" className="max-w-md rounded-2xl border border-status-danger-border bg-white p-8 text-center">
        <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-status-danger" />
        <p className="text-sm text-fundio-ink">{err || "Keine Support-Session. Bitte aus Control „Cockpit ansehen“ starten."}</p>
        <button data-testid="cockpit-error-back" onClick={backToControl} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-fundio-dark2 px-4 py-2 text-sm font-semibold text-fundio-cream">
          <ArrowLeft className="h-4 w-4" /> Zurück zu Control
        </button>
      </div>
    </div>
  );

  if (!session || !data) return (
    <div className="flex min-h-screen items-center justify-center bg-fundio-dark2"><Loader2 className="h-7 w-7 animate-spin text-fundio-gold" /></div>
  );

  const org = data.organization;
  const isMulti = data.is_multi;
  const shown = !isMulti || sel === "all" ? null : data.locations.find((l) => l.id === sel);
  const exp = session.expires_at ? new Date(session.expires_at) : null;

  return (
    <div className="min-h-screen bg-fundio-subtle pb-16">
      {/* SUPER-ADMIN Banner */}
      <div data-testid="cockpit-banner" className="sticky top-0 z-40 border-b border-fundio-gold/40 bg-fundio-dark2 text-fundio-cream shadow-lg">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3">
          <span className="inline-flex items-center gap-2 rounded-md bg-fundio-gold px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-fundio-dark2"><Eye className="h-3.5 w-3.5" />Super-Admin Ansicht</span>
          <span data-testid="cockpit-org-name" className="text-sm font-semibold">{org.company_name}</span>
          <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[org.plan] || PLAN_CLS.FREE}`} data-testid="cockpit-plan">{org.plan || "—"}</span>
          <span data-testid="cockpit-mode" className="inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/10 px-2 py-0.5 text-xs font-mono">{org.account_mode || "single"}</span>
          <span data-testid="cockpit-readonly-badge" className="inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/10 px-2 py-0.5 text-xs">read-only (VIEW)</span>
          {shown && <span className="text-xs text-fundio-cream/70">· Standort: {shown.name}</span>}
          <div className="ml-auto flex items-center gap-2">
            {exp && <span className="text-[11px] text-fundio-cream/60">gültig bis {exp.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}</span>}
            <button data-testid="cockpit-end-button" onClick={endSession} className="inline-flex items-center gap-1 rounded-lg border border-white/25 px-2.5 py-1.5 text-xs font-medium hover:bg-white/10"><LogOut className="h-3.5 w-3.5" />Session beenden</button>
            <button data-testid="cockpit-back-button" onClick={backToControl} className="inline-flex items-center gap-1 rounded-lg bg-fundio-gold px-2.5 py-1.5 text-xs font-semibold text-fundio-dark2 hover:bg-fundio-gold-highlight"><ArrowLeft className="h-3.5 w-3.5" />Zurück zu Control</button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 pt-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-fundio-dark2 text-fundio-gold">{isMulti ? <Layers className="h-5 w-5" /> : <Store className="h-5 w-5" />}</div>
          <div>
            <h1 className="text-xl font-semibold text-fundio-ink" data-testid="cockpit-title">{isMulti ? "MULTI-Zentrale" : "Cockpit"}</h1>
            <p className="text-sm text-fundio-muted">{org.company_name}{org.city ? ` · ${org.city}` : ""}{data.is_qa ? " · INTERNAL QA" : ""}</p>
          </div>
        </div>

        {/* MULTI: Standort-Wechsler */}
        {isMulti && (
          <div data-testid="cockpit-location-switcher" className="mb-6 flex flex-wrap gap-2">
            <button data-testid="cockpit-loc-all" onClick={() => setSel("all")} className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${sel === "all" ? "border-fundio-gold bg-fundio-gold/15 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-ink hover:border-fundio-gold"}`}>Alle Standorte</button>
            {data.locations.map((l) => (
              <button key={l.id} data-testid={`cockpit-loc-${l.id}`} onClick={() => setSel(l.id)} className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${sel === l.id ? "border-fundio-gold bg-fundio-gold/15 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-ink hover:border-fundio-gold"}`}>{l.name}</button>
            ))}
          </div>
        )}

        {/* Kennzahlen */}
        {(!isMulti || sel === "all") && (
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="cockpit-metrics">
            <Metric icon={Layers} label="Standorte" value={isMulti ? data.locations.length : 1} testid="cockpit-metric-locations" />
            <Metric icon={Package} label="Inserate" value={org.item_count} testid="cockpit-metric-items" />
            <Metric icon={MessageSquare} label="Anfragen" value={org.inquiry_count} testid="cockpit-metric-inquiries" />
            <Metric icon={Globe} label="Status" value={org.status || "—"} testid="cockpit-metric-status" />
          </div>
        )}

        {/* Single cockpit / bzw. Standort-Detail */}
        {isMulti && shown ? (
          <div data-testid="cockpit-location-detail" className="rounded-2xl border border-fundio-border bg-white p-6">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-fundio-ink"><MapPin className="h-4 w-4 text-fundio-gold" />{shown.name}</div>
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div><dt className="text-xs text-fundio-muted">Adresse</dt><dd className="text-fundio-ink">{[shown.street, `${shown.postal_code || ""} ${shown.city || ""}`.trim(), shown.canton].filter(Boolean).join(", ") || "—"}</dd></div>
              <div><dt className="text-xs text-fundio-muted">Telefon</dt><dd className="text-fundio-ink">{shown.phone || "—"}</dd></div>
              <div><dt className="text-xs text-fundio-muted">E-Mail</dt><dd className="text-fundio-ink">{shown.email || "—"}</dd></div>
              <div><dt className="text-xs text-fundio-muted">Öffnungszeiten</dt><dd className="whitespace-pre-line text-fundio-ink">{Array.isArray(shown.opening_hours) ? shown.opening_hours.map((p) => Array.isArray(p) ? p.join(": ") : p).join("\n") : (shown.opening_hours || "—")}</dd></div>
            </dl>
          </div>
        ) : (
          <div data-testid={isMulti ? "cockpit-all-locations" : "cockpit-single"} className="rounded-2xl border border-fundio-border bg-white p-6">
            <div className="mb-4 text-sm font-semibold text-fundio-ink">{isMulti ? "Alle Standorte" : "Standort"}</div>
            {isMulti ? (
              <ul className="divide-y divide-fundio-border">
                {data.locations.map((l) => (
                  <li key={l.id} className="flex items-center justify-between py-3">
                    <div><div className="text-sm font-medium text-fundio-ink">{l.name}</div><div className="text-xs text-fundio-muted">{[l.street, `${l.postal_code || ""} ${l.city || ""}`.trim()].filter(Boolean).join(", ")}</div></div>
                    <button onClick={() => setSel(l.id)} className="text-xs font-semibold text-fundio-gold hover:underline">Öffnen</button>
                  </li>
                ))}
                {!data.locations.length && <li className="py-3 text-sm text-fundio-muted">Keine Standorte hinterlegt.</li>}
              </ul>
            ) : (
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div><dt className="text-xs text-fundio-muted">Firma</dt><dd className="text-fundio-ink">{org.company_name}</dd></div>
                <div><dt className="text-xs text-fundio-muted">Ort</dt><dd className="text-fundio-ink">{org.city || "—"}</dd></div>
                <div><dt className="text-xs text-fundio-muted">Plan</dt><dd className="text-fundio-ink">{org.plan || "—"}</dd></div>
                <div><dt className="text-xs text-fundio-muted">Website</dt><dd className="truncate text-fundio-ink">{org.external_website || "—"}</dd></div>
              </dl>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-fundio-faint">Read-only Support-Ansicht · keine Schreibaktionen · Session endet automatisch nach Ablauf.</p>
      </div>
    </div>
  );
}
